import React from 'react';

import type { InsurancePolicy } from '@/types/insurancePolicy';
import type { InsuranceVerification } from '@/types/insuranceVerification';
import { Stat } from '@/ui';
import { currencyInCentsFormatter } from '@/utils/currencies';

import './styles.css';
import type { PolicyFinancialsSnapshot, Props } from './types';

const POLICY_RECORD_NOTE = 'on policy record';

export const policyFinancialsSnapshot = (
  policy: InsurancePolicy,
  verification?: InsuranceVerification | null
): PolicyFinancialsSnapshot => {
  const finance = verification?.external_policy_verification?.finance;

  return {
    deductibleRemainingInCents:
      verification?.deductible_remaining ?? finance?.deductible_remaining_in_cents ?? null,
    outOfPocketRemainingInCents:
      verification?.out_of_pocket_maximum_remaining_in_cents ??
      finance?.out_of_pocket_remaining_in_cents ??
      null,
    estimatedMonthlyCostInCents: verification?.estimated_monthly_cost_in_cents ?? null,
    coinsurancePercentage:
      finance?.coinsurance_percentage ??
      (policy.co_insurance != null ? Math.round(policy.co_insurance * 100) : null)
  };
};

const remainingSublabel = (
  remainingInCents: number | null,
  totalInCents: number | null,
  isFromPolicyRecord: boolean
) => {
  if (totalInCents == null || totalInCents <= 0) return null;

  const parts = [`of ${currencyInCentsFormatter(totalInCents)}`];
  if (remainingInCents != null) {
    const met = Math.min(100, Math.max(0, Math.round((1 - remainingInCents / totalInCents) * 100)));
    parts.push(`${met}% met`);
  }
  if (isFromPolicyRecord) parts.push(POLICY_RECORD_NOTE);

  return parts.join(' · ');
};

const progressFor = (remainingInCents: number | null, totalInCents: number | null) =>
  remainingInCents != null && totalInCents != null && totalInCents > 0
    ? 1 - remainingInCents / totalInCents
    : null;

const changed = (current: number | null, previous: number | null | undefined) =>
  previous != null && current != null && previous !== current;

const PolicyFinancials = ({
  policy,
  verification,
  changedFrom,
  isMuted = false,
  className
}: Props) => {
  const finance = verification?.external_policy_verification?.finance;
  const current = policyFinancialsSnapshot(policy, verification);

  const deductibleTotal =
    finance?.deductible_in_cents ??
    (policy.annual_deductible != null ? policy.annual_deductible * 100 : null);
  const isDeductibleTotalFromPolicy =
    finance?.deductible_in_cents == null && policy.annual_deductible != null;

  const oopTotal = finance?.out_of_pocket_max_in_cents ?? policy.out_of_pocket_maximum ?? null;
  const isOopTotalFromPolicy =
    finance?.out_of_pocket_max_in_cents == null && policy.out_of_pocket_maximum != null;

  const isCoinsuranceFromPolicy =
    finance?.coinsurance_percentage == null && policy.co_insurance != null;

  const copayParts: string[] = [];
  if (finance?.office_copay_in_cents != null) {
    copayParts.push(`${currencyInCentsFormatter(finance.office_copay_in_cents)} office`);
  }
  if (finance?.specialist_copay_in_cents != null) {
    copayParts.push(`${currencyInCentsFormatter(finance.specialist_copay_in_cents)} specialist`);
  }
  if (!copayParts.length && policy.co_pay != null) {
    copayParts.push(
      `${currencyInCentsFormatter(policy.co_pay)}${policy.co_pay_unit ? ` ${policy.co_pay_unit}` : ''}`,
      POLICY_RECORD_NOTE
    );
  }

  const tone = isMuted ? ('muted' as const) : undefined;
  const asCurrency = (cents: number | null) =>
    cents == null ? null : currencyInCentsFormatter(cents);

  return (
    <div className={`grid grid-cols-2 gap-2 lg:grid-cols-4 ${className ?? ''}`}>
      <Stat
        label="Deductible remaining"
        value={asCurrency(current.deductibleRemainingInCents)}
        progress={isMuted ? null : progressFor(current.deductibleRemainingInCents, deductibleTotal)}
        sublabel={remainingSublabel(
          current.deductibleRemainingInCents,
          deductibleTotal,
          isDeductibleTotalFromPolicy
        )}
        tone={tone}
        changedFrom={
          changed(current.deductibleRemainingInCents, changedFrom?.deductibleRemainingInCents)
            ? asCurrency(changedFrom?.deductibleRemainingInCents ?? null)
            : undefined
        }
      />
      <Stat
        label="OOP remaining"
        value={asCurrency(current.outOfPocketRemainingInCents)}
        progress={isMuted ? null : progressFor(current.outOfPocketRemainingInCents, oopTotal)}
        sublabel={remainingSublabel(
          current.outOfPocketRemainingInCents,
          oopTotal,
          isOopTotalFromPolicy
        )}
        tone={tone}
        changedFrom={
          changed(current.outOfPocketRemainingInCents, changedFrom?.outOfPocketRemainingInCents)
            ? asCurrency(changedFrom?.outOfPocketRemainingInCents ?? null)
            : undefined
        }
      />
      <Stat
        label="Est. monthly"
        value={asCurrency(current.estimatedMonthlyCostInCents)}
        sublabel={
          verification?.estimated_initial_month_cost_in_cents != null
            ? `First month ${currencyInCentsFormatter(verification.estimated_initial_month_cost_in_cents)}`
            : null
        }
        tone={tone}
        changedFrom={
          changed(current.estimatedMonthlyCostInCents, changedFrom?.estimatedMonthlyCostInCents)
            ? asCurrency(changedFrom?.estimatedMonthlyCostInCents ?? null)
            : undefined
        }
      />
      <Stat
        label="Cost share"
        value={current.coinsurancePercentage != null ? `${current.coinsurancePercentage}%` : null}
        sublabel={
          copayParts.length
            ? copayParts.join(' · ')
            : isCoinsuranceFromPolicy
              ? POLICY_RECORD_NOTE
              : null
        }
        tone={tone}
        changedFrom={
          changed(current.coinsurancePercentage, changedFrom?.coinsurancePercentage)
            ? `${changedFrom?.coinsurancePercentage}%`
            : undefined
        }
      />
    </div>
  );
};

export default React.memo(PolicyFinancials);
